import { Injectable } from '@angular/core';
import { RestService } from '@app/core/rest.service';
import { Observable } from 'rxjs/Observable';
import { Kyc } from '@app/core/models/kyc';
import { AgentInfo } from './models/agent';
import { Subject } from 'rxjs';
import * as _ from 'lodash';
import { getOptionsFromQuestionRaws } from '@app/features/coverage/coverage';
import * as moment from 'moment';

@Injectable()
export class KycService extends RestService {
  private _relativeUrl = 'api/kyc';
  private _relativeUrlNationalityList = "api/Nationality/NationalityList";
  kyc: Kyc = new Kyc();
  agentInfoObservable: Subject<AgentInfo> = new Subject();
  browserRefresh = false;

  agentInfo: AgentInfo;

  getAgentInfo() {
    return this.agentInfoObservable.asObservable();
  }

  saveKyc(payload): Promise<any> {
    const apiPayload = {
      agentId: this.agentInfo.id,
      profile: null,
      policies: null,
      needs: null,
    };

    const coverage = payload.coverage;
    if (!coverage) {
      throw new Error('coverage has not been created');
    }
    const policies = new Array<any>();
    _.forEach(coverage.insurances, (item) => {
      if (item.policies.length > 0) {
        const clonnedPolicies = _.cloneDeep(item.policies);
        clonnedPolicies.forEach(policy => {
          const options = getOptionsFromQuestionRaws(item.questionRaws);
          if (!policy.typeOfAsset) {
            policy.typeOfInsurance = options.filter(opt => opt.key === policy.typeOfInsurance)[0].miValue;
          } else {
            const filteredOptions = options.filter(opt => {
              if (opt.dependsOn) {
                return opt.dependsOn.value === policy.typeOfAsset;
              }
            });
            policy.typeOfInsurance = filteredOptions.filter(opt => opt.key === policy.typeOfInsurance)[0].miValue;
          }

          if (policy.ownerStatus) {
            policy.ownerStatus = options.filter(opt => opt.key === policy.ownerStatus)[0].miValue;
          }
        });

        policies.push(clonnedPolicies);
      } else if (item.actived) {
        policies.push(defaultInsurancePolicy[item.key]);
      }
    });

    apiPayload.policies = _.flattenDepth(policies);

    const needs = payload.needs.needs;
    if (!needs) {
      throw new Error('needs has not been created');
    }

    const myNeeds = {};
    _.forEach(needs, (needsItem) => {
      const needsPart = {};
      _.forEach(needsItem.questionRaws, (question) => {
        needsPart[question.key] = question.value;
      });

      myNeeds[needsItem.key] = needsPart;
    });

    apiPayload.needs = myNeeds;

    const save = payload.save.save;
    if (!save) {
      throw new Error('save has not been created');
    }
    apiPayload.profile = {};
    _.forEach(save, (saveItem) => {
      apiPayload.profile[saveItem.key] = saveItem.value;
    });

    const profileTab = payload.profile.userProfile;
    apiPayload.profile['dateOfBirth'] = moment(payload.profile.dob).format('DD/MM/YYYY');
    apiPayload.profile['email'] = profileTab.email;
    apiPayload.profile['gender'] = Number(profileTab.gender) === 0 ? "FEMALE" : "MALE";
    apiPayload.profile['kids'] = profileTab.kids;
    apiPayload.profile['marital'] = Number(profileTab.marital) === 0 ? 1 : 0;

    console.log('Payload to save kyc');
    console.log(apiPayload);

    const url = `/api/hub`;
    return new Promise((resolve, reject) => {
      this.post(url, {
        payload: JSON.stringify(apiPayload),
        apiUrl: 'api/kyc/savekyc'
      }, true).subscribe((value) => {
        if (value.data.Info.Result === 'ERROR') {
          return reject(value.data.Info.Message);
        }

        return resolve(value);
      });
    });
  }
  getNationalityList(): Observable<any> {
    const encodedUri = encodeURI(`${this._relativeUrlNationalityList}`);
    return this.get(`/api/hub?path=${encodedUri}`, true);
  }
  getAgent(referalCode): Observable<any> {
    if (!referalCode) {
      return new Observable<object>(observer => {
        const admin = {
          data: {
            Content: [
              {
                Data: {
                  AgentId: '00000000-0000-0000-0000-000000000000',
                  FirstName: 'Admin',
                  LastName: ''
                }
              }
            ]
          }
        };

        observer.next(admin);
      });
    }
    const encodedUri = encodeURI(`${this._relativeUrl}/${referalCode}`);
    return this.get(`/api/hub?path=${encodedUri}`, true);
  }

  getToken(): Observable<any> {
    return this.get('/api/hub/token', true);
  }
}

const defaultInsurancePolicy = {
  healthInsurances: {
    medicalExpensesAccident: 0,
    medicalExpensesOther: 0,
    productCategory: 'Health Insurance',
    renewableDate: moment().utc().add(1, 'year').format('DD/MM/YYYY'),
    sumInsuredAccidentalDeath: 0,
    sumInsuredAccidentalPermanentDisablement: 0,
    sumInsuredCriticalIllness: 0,
    sumInsuredEarlyCriticalIllness: 0,
    totalSumInsured: 0,
    typeOfInsurance: 1
  },
  PAInsurances: {
    medicalExpensesAccident: 0,
    medicalExpensesOther: 0,
    productCategory: 'Personal Accident Insurance',
    renewableDate: moment().utc().add(1, 'year').format('DD/MM/YYYY'),
    sumInsuredAccidentalDeath: 0,
    sumInsuredAccidentalPermanentDisablement: 0,
    sumInsuredCriticalIllness: 0,
    sumInsuredEarlyCriticalIllness: 0,
    totalSumInsured: 0,
    typeOfInsurance: 1
  },
  assetInsurances: {
    address1: '',
    medicalExpensesAccident: 0,
    medicalExpensesOther: 0,
    ownerStatus: 0,
    postcode: '',
    premium: 0,
    productCategory: 'Motor Insurance',
    propertyType: '',
    renewableDate: moment().utc().add(1, 'year').format('DD/MM/YYYY'),
    sumInsuredAccidentalDeath: 0,
    sumInsuredAccidentalPermanentDisablement: 0,
    typeOfAsset: 'motor',
    typeOfInsurance: 1
  },
  lifeInsurances: {
    medicalExpensesAccident: 0,
    medicalExpensesOther: 0,
    productCategory: 'Life Insurance',
    renewableDate: moment().utc().add(1, 'year').format('DD/MM/YYYY'),
    sumInsuredAccidentalDeath: 0,
    sumInsuredAccidentalPermanentDisablement: 0,
    sumInsuredCriticalIllness: 0,
    sumInsuredEarlyCriticalIllness: 0,
    sumInsuredLifeDeath: 0,
    sumInsuredPermanentDisablementLife: 0,
    totalSumInsured: 0,
    typeOfInsurance: 1
  }
};



// WEBPACK FOOTER //
// ./src/app/core/kyc.service.ts